// apps/landing-page/src/lib/cart-sync.ts
import type { CartItem } from './cart-storage';
import { ensureAnonId } from './anon-id';
import { hasAnalyticsConsent } from './consent';

export type CartSyncStatus = 'ACTIVE' | 'CHECKED_OUT' | 'CLEARED';

type SyncEntityType = 'TEST_MODULE' | 'BUNDLE' | 'COURSE';

interface SyncItem {
  entityType: SyncEntityType;
  entityId: string;
  priceAtAdd: number;
}

/**
 * Maps a cart item's product kind to the cart-sync contract. `event` (live-event tickets)
 * has no entityType in the shared contract — it goes through its own reservation flow, not
 * generic cart-sync analytics — so it's filtered out by returning null.
 */
function toSyncEntityType(type: CartItem['type']): SyncEntityType | null {
  switch (type) {
    case 'course':
      return 'COURSE';
    case 'bundle':
      return 'BUNDLE';
    case 'module':
    case undefined:
      return 'TEST_MODULE';
    default:
      return null;
  }
}

/**
 * Fire-and-forget side effect that tells the backend what's in the cart, so it can attribute
 * cart contents to a logged-in session or (via `aks_anon`) an anonymous visitor for analytics.
 * Never awaited by callers and never throws into cart UX — network/API failures are swallowed.
 */
export function syncCart(items: CartItem[], status: CartSyncStatus): void {
  if (typeof document === 'undefined') return;
  // Analytics is opt-in: without consent, send nothing and never mint aks_anon. This ran
  // on every page load (an empty cart reports CLEARED), so it set the cookie before the
  // visitor had answered the consent bar.
  if (!hasAnalyticsConsent()) return;

  const anonId = ensureAnonId();
  const syncItems: SyncItem[] = [];
  for (const item of items) {
    const entityType = toSyncEntityType(item.type);
    if (!entityType) continue;
    syncItems.push({ entityType, entityId: item.moduleId, priceAtAdd: item.price });
  }

  fetch('/api/proxy/cart/sync', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // Belt-and-suspenders alongside the aks_anon cookie (sent automatically as it's an
      // apex cookie) — the backend reads either.
      'x-anon-id': anonId,
    },
    body: JSON.stringify({ items: syncItems, status }),
  }).catch(() => {});
}
